/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useHistory, useLocation} from 'react-router-dom'
import {StorefrontPreview} from '@salesforce/commerce-sdk-react/components'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useQuery, useQueries} from '@tanstack/react-query'
import {
    useAccessToken,
    // useCategory,
    useCommerceApi,
    useCustomerBaskets,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import * as queryKeyHelpers from '@salesforce/commerce-sdk-react/hooks/ShopperProducts/queryKeyHelpers'
// Chakra
import {
    Box,
    Flex,
    useDisclosure,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {SkipNavLink, SkipNavContent} from '@chakra-ui/skip-nav'

// Contexts
import {CurrencyProvider} from '@salesforce/retail-react-app/app/contexts'
import {HeaderProvider} from '@consteed/frontend/contexts/HeaderContext'

// Local Project Components
import Header from '@consteed/overrides/components/header'
import OfflineBanner from '@salesforce/retail-react-app/app/components/offline-banner'
import OfflineBoundary from '@salesforce/retail-react-app/app/components/offline-boundary'
import ScrollToTop from '@salesforce/retail-react-app/app/components/scroll-to-top'
import Footer from '@consteed/overrides/components/footer'
import CheckoutHeader from '@consteed/overrides/pages/checkout/partials/checkout-header'
import CheckoutFooter from '@consteed/overrides/pages/checkout/partials/checkout-footer'
import DrawerMenu from '@consteed/overrides/components/drawer-menu'
import ListMenu from '@consteed/overrides/components/list-menu'
import {HideOnDesktop, HideOnMobile} from '@consteed/overrides/components/responsive'
import AboveHeader from '@salesforce/retail-react-app/app/components/_app/partials/above-header'

// Hooks
import {AuthModal, useAuthModal} from '@consteed/overrides/hooks/use-auth-modal'
import {AddToCartModalProvider} from '@consteed/overrides/hooks/use-add-to-cart-modal'
import {SelectRoleModalProvider} from '@consteed/frontend/hooks/use-select-role-modal'
import {WithdrawalModalProvider} from '@consteed/frontend/hooks/use-withdrawal-modal'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {usePage} from '@salesforce/commerce-sdk-react'

// Localization
import {IntlProvider} from 'react-intl'

// Others
import {watchOnlineStatus, isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {getTargetLocale, fetchTranslations} from '@salesforce/retail-react-app/app/utils/locale'
import {
    DEFAULT_SITE_TITLE,
    HOME_HREF,
    THEME_COLOR,
    DEFAULT_LOCALE,
    ACTIVE_DATA_ENABLED
} from '@consteed/overrides/constants'
import {hrefToLinkObject} from '@consteed/frontend/utils/richTextConfigurations'

import Seo from '@salesforce/retail-react-app/app/components/seo'
import {Helmet} from 'react-helmet'
import {FormattedMessage} from 'react-intl'
import {getConsentManagerConfig} from '@consteed/frontend/utils/consentManager'

const convertPwaLinkTile = (linkTile) => {
    let linkListElement = hrefToLinkObject(linkTile?.data?.link)

    linkListElement.image = linkTile?.data?.image
    linkListElement.alt = linkTile?.data?.alt
    linkListElement.c_showInMenu = true
    linkListElement.text = linkTile?.data?.text
    linkListElement.backgroundTheme = linkTile?.data?.backgroundTheme

    return linkListElement
}

const convertPwaLink = (link) => {
    let linkListElement = {
        name: link?.data?.linkText,
        linkType: link?.data?.linkType,
        c_showInMenu: true
    }
    switch (link?.data?.linkType) {
        case 'Page':
            if (!link?.data?.page) {
                return null
            }
            linkListElement.id = link?.data?.page
            break
        case 'Product':
            if (!link?.data?.product) {
                return null
            }
            linkListElement.id = link?.data?.product
            break
        case 'Category':
            if (!link?.data?.category) {
                return null
            }
            linkListElement.id = link?.data?.category
            break
        case 'Static':
            if (!link?.data?.staticResource) {
                return null
            }
            linkListElement.id = 'static'
            linkListElement.href = link?.data?.staticResource
            break
        case 'Blog':
            linkListElement.id = 'blog'
            break
        case 'ContactUs':
            linkListElement.id = 'contact'
            break
        case 'External':
        // falls through
        default:
            if (!link?.data?.externalUrl) {
                return null
            }
            linkListElement = hrefToLinkObject(link?.data?.externalUrl)
            linkListElement.c_showInMenu = true
    }
    return linkListElement
}

const convertSubMenu = (component) => {
    const contentRegion = component.regions.find((region) => {
        return region.id === 'content'
    })
    const linkRegion = component.regions.find((region) => {
        return region.id === 'links'
    })

    return {
        id: component.id,
        linkType: 'product',
        name: contentRegion?.components?.[0]?.data?.linkText,
        categories: linkRegion ? convertLinkRegion(linkRegion) : [],
        c_showInMenu: true
    }
}

const convertLinkRegion = (region) => {
    return region?.components
        ?.map((component) => {
            switch (component.typeId) {
                case 'custom_assets.pwaLink':
                    return convertPwaLink(component)
                case 'custom_layouts.pwaSubmenuWrapper':
                    return convertSubMenu(component)
                case 'custom_assets.pwaLinkTile':
                    return convertPwaLinkTile(component)
                default:
                    return null
            }
        })
        .filter((element) => element)
}

const convertPageRegion = (region) => {
    return convertLinkRegion(region?.components?.[0]?.regions?.[0])
}

const convertPageToNavigationTree = (page) => {
    const navTree = {
        firstHeaderMenuCol: {
            categories: []
        },
        secondHeaderMenuCol: {
            categories: []
        },
        burgerMenuCol: {
            categories: []
        }
    }

    if (page?.regions) {
        const firstRegion = page.regions.find((region) => {
            return region.id === 'first'
        })
        const secondRegion = page.regions.find((region) => {
            return region.id === 'second'
        })
        if (firstRegion) {
            navTree.firstHeaderMenuCol.categories = convertPageRegion(firstRegion)
        }
        if (secondRegion) {
            navTree.secondHeaderMenuCol.categories = convertPageRegion(secondRegion)
        }
        navTree.burgerMenuCol.categories = navTree.firstHeaderMenuCol.categories.concat(
            navTree.secondHeaderMenuCol.categories
        )
        // TODO make sure firstHeaderMenuCol and secondHeaderMenuCol do not have more than 3 categories
    }
    return navTree
}

const App = (props) => {
    const {children} = props
    const {data: page} = usePage({parameters: {pageId: 'headermenu'}})
    const categories = convertPageToNavigationTree(page)

    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = getAppOrigin()
    const activeData = useActiveData()

    const history = useHistory()
    const location = useLocation()
    const isPDP = /\/product\//.test(location?.pathname)
    const currentPage = isPDP
        ? 'product'
        : location?.pathname.substring(location?.pathname.lastIndexOf('/') + 1)
    const authModal = useAuthModal()
    const {site, locale, buildUrl} = useMultiSite()

    const [isOnline, setIsOnline] = useState(true)
    const styles = useStyleConfig('App')

    const {isOpen, onOpen, onClose} = useDisclosure()

    const targetLocale = getTargetLocale({
        getUserPreferredLocales: () => {
            // CONFIG: This function should return an array of preferred locales. They can be
            // derived from various sources. Below are some examples of those:
            //
            // - client side: window.navigator.languages
            // - the page URL they're on (example.com/en-GB/home)
            // - cookie (if their previous preference is saved there)
            //
            // If this function returns an empty array (e.g. there isn't locale in the page url),
            // then the app would use the default locale as the fallback.

            // NOTE: Your implementation may differ, this is just what we did.
            return [locale?.id || DEFAULT_LOCALE]
        },
        l10nConfig: site.l10n
    })

    // If the translation file exists, it'll be served directly from static folder (and won't reach this code here).
    // However, if the file is missing, the App would render a 404 page.
    const is404ForMissingTranslationFile = /\/static\/translations\/compiled\/[^.]+\.json$/.test(
        location?.pathname
    )

    // Fetch the translation message data using the target locale.
    const {data: messages} = useQuery({
        queryKey: ['app', 'translations', 'messages', targetLocale],
        queryFn: () => {
            if (is404ForMissingTranslationFile) {
                // Return early to prevent an infinite loop
                // Otherwise, it'll continue to fetch the missing translation file again
                return {}
            }
            return fetchTranslations(targetLocale)
        },
        enabled: isServer
    })

    // Used to conditionally render header/footer for checkout page
    const isCheckout = /\/checkout$/.test(location?.pathname)

    const {l10n} = site
    // Get the current currency to be used through out the app
    const currency = locale.preferredCurrency || l10n.defaultCurrency

    // Consent Manager
    const cmp = getConsentManagerConfig()

    // Handle creating a new basket if there isn't one already assigned to the current
    // customer.
    const {data: customer} = useCurrentCustomer()
    const {data: baskets} = useCustomerBaskets(
        {parameters: {customerId: customer.customerId}},
        {enabled: !!customer.customerId && !isServer}
    )
    const {data: basket} = useCurrentBasket()

    const createBasket = useShopperBasketsMutation('createBasket')
    const updateBasket = useShopperBasketsMutation('updateBasket')
    const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket')

    useEffect(() => {
        // Create a new basket if the current customer doesn't have one.
        if (baskets?.total === 0) {
            createBasket.mutate({
                body: {}
            })
        }
    }, [baskets])

    useEffect(() => {
        // update the basket currency if it doesn't match the current locale currency
        if (basket?.currency && basket?.currency !== currency) {
            updateBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {currency}
            })
        }
    }, [basket?.currency])

    useEffect(() => {
        // update the basket customer email
        if (
            basket &&
            customer?.isRegistered &&
            customer?.email &&
            customer?.email !== basket?.customerInfo?.email
        ) {
            updateCustomerForBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {
                    email: customer.email
                }
            })
        }
    }, [customer?.isRegistered, customer?.email, basket?.customerInfo?.email])

    useEffect(() => {
        // Listen for online status changes.
        watchOnlineStatus((isOnline) => {
            setIsOnline(isOnline)
        })
        // configure cmp custom languages
        if (cmp.enabled && cmp.customLanguages) {
            window.cmp_customlanguages = cmp.customLanguages
        }
    }, [])

    useEffect(() => {
        // Lets automatically close the mobile navigation when the
        // location path is changed.
        onClose()
    }, [location])

    const onLogoClick = () => {
        // Goto the home page.
        const path = buildUrl(HOME_HREF)

        history.push(path)

        // Close the drawer.
        onClose()
    }

    const onCartClick = () => {
        const path = buildUrl('/cart')
        history.push(path)

        // Close the drawer.
        onClose()
    }

    const onAccountClick = () => {
        // Link to account page if registered; Header component will show auth modal for guest users
        const path = buildUrl('/account')
        history.push(path)
    }

    const trackPage = () => {
        activeData.trackPage(site.id, locale.id, currency)
    }

    useEffect(() => {
        trackPage()
    }, [location])

    return (
        <Box className="sf-app" {...styles.container}>
            <StorefrontPreview getToken={getTokenWhenReady}>
                <Helmet>
                    {ACTIVE_DATA_ENABLED && (
                        <script
                            src={getAssetUrl('static/head-active_data.js')}
                            id="headActiveData"
                            type="text/javascript"
                        ></script>
                    )}
                    {cmp?.enabled && (
                        <script
                            type="text/javascript"
                            data-cmp-ab="1"
                            src={cmp.url}
                            data-cmp-host="b.delivery.consentmanager.net"
                            data-cmp-cdn="cdn.consentmanager.net"
                            data-cmp-codesrc="1"
                        ></script>
                    )}
                </Helmet>
                <IntlProvider
                    onError={(err) => {
                        if (!messages) {
                            // During the ssr prepass phase the messages object has not loaded, so we can suppress
                            // errors during this time.
                            return
                        }
                        if (err.code === 'MISSING_TRANSLATION') {
                            // NOTE: Remove the console error for missing translations during development,
                            // as we knew translations would be added later.
                            console.warn('Missing translation', err.message)
                            return
                        }
                        throw err
                    }}
                    locale={targetLocale}
                    messages={messages}
                    // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
                    // NOTE: if you update this value, please also update the following npm scripts in `template-retail-react-app/package.json`:
                    // - "extract-default-translations"
                    // - "compile-translations:pseudo"
                    defaultLocale={DEFAULT_LOCALE}
                >
                    <CurrencyProvider currency={currency}>
                        <HeaderProvider>
                            <WithdrawalModalProvider>
                                <SelectRoleModalProvider>
                                <Seo>
                                    <meta name="theme-color" content={THEME_COLOR} />
                                    <meta
                                        name="apple-mobile-web-app-title"
                                        content={DEFAULT_SITE_TITLE}
                                    />
                                    <link
                                        rel="apple-touch-icon"
                                        sizes="180x180"
                                        href={getAssetUrl('static/img/global/apple-touch-icon.png')}
                                    />
                                    <link
                                        rel="icon"
                                        type="image/png"
                                        sizes="32x32"
                                        href={getAssetUrl('static/img/global/favicon-32x32.png')}
                                    />
                                    <link
                                        rel="icon"
                                        type="image/png"
                                        sizes="16x16"
                                        href={getAssetUrl('static/img/global/favicon-16x16.png')}
                                    />
                                    <link
                                        rel="mask-icon"
                                        href={getAssetUrl(
                                            'static/img/global/safari-pinned-tab.svg'
                                        )}
                                        color="#5bbad5"
                                    />
                                    <meta name="msapplication-TileColor" content="#da532c" />
                                    <meta name="theme-color" content="#ffffff" />

                                    <link
                                        rel="manifest"
                                        href={getAssetUrl('static/manifest.json')}
                                    />

                                    {/* Urls for all localized versions of this page (including current page)
                                For more details on hrefLang, see https://developers.google.com/search/docs/advanced/crawling/localized-versions */}
                                    {site.l10n?.supportedLocales.map((locale) => (
                                        <link
                                            rel="alternate"
                                            hrefLang={locale.id.toLowerCase()}
                                            href={`${appOrigin}${buildUrl(location.pathname)}`}
                                            key={locale.id}
                                        />
                                    ))}
                                    {/* A general locale as fallback. For example: "en" if default locale is "en-GB" */}
                                    <link
                                        rel="alternate"
                                        hrefLang={site.l10n.defaultLocale.slice(0, 2)}
                                        href={`${appOrigin}${buildUrl(location.pathname)}`}
                                    />
                                    {/* A wider fallback for user locales that the app does not support */}
                                    <link
                                        rel="alternate"
                                        hrefLang="x-default"
                                        href={`${appOrigin}/`}
                                    />
                                </Seo>

                                <ScrollToTop />

                                <Box id="app" display="flex" flexDirection="column" flex={1}>
                                    <SkipNavLink zIndex="skipLink">
                                        <FormattedMessage
                                            defaultMessage="Skip to Content"
                                            id="global.ay11.skip_to_content"
                                        />
                                    </SkipNavLink>

                                    <Box {...styles.headerWrapper}>
                                        {!isCheckout ? (
                                            <>
                                                <AboveHeader />
                                                <Header
                                                    onMenuClick={onOpen}
                                                    onLogoClick={onLogoClick}
                                                    onMyCartClick={onCartClick}
                                                    onMyAccountClick={onAccountClick}
                                                >
                                                    <HideOnDesktop>
                                                        <DrawerMenu
                                                            isOpen={isOpen}
                                                            onClose={onClose}
                                                            onLogoClick={onLogoClick}
                                                            root={categories?.burgerMenuCol}
                                                            activePage={currentPage}
                                                        />
                                                    </HideOnDesktop>

                                                    <HideOnMobile>
                                                        <Flex justify={'space-between'}>
                                                            <Flex
                                                                spacing={'2.5rem'}
                                                                className={'leftNavi'}
                                                            >
                                                                <ListMenu
                                                                    root={
                                                                        categories?.firstHeaderMenuCol
                                                                    }
                                                                    activePage={currentPage}
                                                                />
                                                            </Flex>
                                                            <Flex
                                                                spacing={'2.5rem'}
                                                                className={'rightNavi'}
                                                            >
                                                                <ListMenu
                                                                    root={
                                                                        categories?.secondHeaderMenuCol
                                                                    }
                                                                    activePage={currentPage}
                                                                />
                                                            </Flex>
                                                        </Flex>
                                                    </HideOnMobile>
                                                </Header>
                                            </>
                                        ) : (
                                            <CheckoutHeader />
                                        )}
                                    </Box>
                                    {!isOnline && <OfflineBanner />}
                                    <AddToCartModalProvider variant="toast">
                                        <SkipNavContent
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                flex: 1,
                                                outline: 0
                                            }}
                                        >
                                            <Box
                                                as="main"
                                                id="app-main"
                                                role="main"
                                                display="flex"
                                                flexDirection="column"
                                                flex="1"
                                            >
                                                <OfflineBoundary isOnline={false}>
                                                    {children}
                                                </OfflineBoundary>
                                            </Box>
                                        </SkipNavContent>

                                        {!isCheckout ? <Footer /> : <CheckoutFooter />}

                                        <AuthModal {...authModal} />
                                    </AddToCartModalProvider>
                                </Box>
                            </SelectRoleModalProvider>
                            </WithdrawalModalProvider>
                        </HeaderProvider>
                    </CurrencyProvider>
                </IntlProvider>
                {ACTIVE_DATA_ENABLED && (
                    <script
                        type="text/javascript"
                        src={getAssetUrl('static/dwanalytics-22.2.js')}
                        id="dwanalytics"
                        async="async"
                        onLoad={trackPage}
                    ></script>
                )}
                {ACTIVE_DATA_ENABLED && (
                    <script
                        src={getAssetUrl('static/dwac-21.7.js')}
                        type="text/javascript"
                        id="dwac"
                        async="async"
                    ></script>
                )}
            </StorefrontPreview>
        </Box>
    )
}

App.propTypes = {
    children: PropTypes.node
}

/**
 * a hook that parallelly and individually fetches category based on the given ids
 * @param ids - list of categories ids to fetch
 * @param queryOptions -  react query options
 * @return list of react query results
 */
export const useCategoryBulk = (ids = [], queryOptions) => {
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const {
        app: {commerceAPI}
    } = getConfig()
    const {
        parameters: {organizationId}
    } = commerceAPI
    const {site} = useMultiSite()

    const queries = ids.map((id) => {
        return {
            queryKey: queryKeyHelpers.getCategory.queryKey({
                id,
                levels: 2,
                organizationId,
                siteId: site.id
            }),
            queryFn: async () => {
                const token = await getTokenWhenReady()
                const res = await api.shopperProducts.getCategory({
                    parameters: {id, levels: 2},
                    headers: {
                        authorization: `Bearer ${token}`
                    }
                })
                return res
            },
            ...queryOptions,
            enabled: queryOptions.enabled !== false && Boolean(id)
        }
    })
    const res = useQueries({queries})
    return res
}

export default App
